import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { ProductType } from '@tink/core/src/types';
import { GradualReleaseLabels } from '../../utilities/useGradualRolloutTypes';
import { selectUser } from '../Analytics/new-analytics';
import { useGradualRollout } from '../../utilities/useGradualRollout';
import { PageView } from '../../models/analytics';
import { BaseRoutes } from '../BaseRoutes/BaseRoutes';
import { useSelectedProviderName } from '../../components/App/ProviderContainer/useSelectedProvider';
import { buildSingleProviderUrl } from '../../routes/index';
import { ApplicationError } from '../../components/ApplicationErrorBoundary/errors/ApplicationError';
import {
  ProductDimensionAccountCheck,
  ProductDimensionAuthorize,
  ProductDimensionBusinessAccountCheck,
  ProductDimensionCredentials,
  ProductDimensionExpenseCheck,
  ProductDimensionExtendConsent,
  ProductDimensionIncomeCheck,
  ProductDimensionPay,
  ProductDimensionProducts,
  ProductDimensionReports,
  ProductDimensionRiskInsights,
  ProductDimensionTransactions,
} from '../../models/product';
import { applicationParameters } from '../../utilities/application-parameters';
import { ProviderSelectionMachineView } from './ProviderSelectionMachineView';

type ProductProviderSelectionProps = {
  version: string;
  productType: ProductType;
  productDimension:
    | ProductDimensionPay
    | ProductDimensionAuthorize
    | ProductDimensionAccountCheck
    | ProductDimensionBusinessAccountCheck
    | ProductDimensionIncomeCheck
    | ProductDimensionCredentials
    | ProductDimensionTransactions
    | ProductDimensionReports
    | ProductDimensionRiskInsights
    | ProductDimensionProducts
    | ProductDimensionExpenseCheck
    | ProductDimensionExtendConsent;
};

/**
 * Use this component to add the XState provider selection for a product.
 *
 * Once a provider is selected, it will redirect to the `ProviderContainer`
 * product specific URL.
 */
export const ProductProviderSelection = ({
  version,
  productType,
  productDimension,
}: ProductProviderSelectionProps) => {
  const history = useHistory();
  const initialProviderName = useSelectedProviderName();

  return (
    <ProviderSelectionMachineView
      initialProviderName={initialProviderName}
      onProviderSelected={({ provider }) => {
        const url = buildSingleProviderUrl({
          version,
          productType,
          productDimension,
          providerName: provider.name,
        });

        history.push(url);
      }}
      onProviderNotFound={({ error }) => {
        throw new ApplicationError(error);
      }}
      version={version}
    />
  );
};

type ProductProviderSelectionWithRolloutProps = ProductProviderSelectionProps & {
  gradualReleaseLabel: GradualReleaseLabels;
};

/**
 * Use this component to add the XState provider selection for a product, with
 * a gradual rollout.
 *
 * If the user is not assigned to the test group, they will go through the
 * classic URL driven flow.
 *
 * @see ProductProviderSelection
 */
export const ProductProviderSelectionWithRollout = ({
  productType,
  productDimension,
  gradualReleaseLabel,
  version,
}: ProductProviderSelectionWithRolloutProps) => {
  const user = useSelector(selectUser);
  const market = applicationParameters.get('market');

  const { isInTestGroup } = useGradualRollout(PageView.NO_SCREEN, gradualReleaseLabel);
  const isUserInTestGroup = isInTestGroup({ userId: user?.id || '', market });

  if (isUserInTestGroup) {
    return (
      <ProductProviderSelection
        version={version}
        productType={productType}
        productDimension={productDimension}
      />
    );
  }

  return <BaseRoutes />;
};
